import type { RestType, SessionExercise } from '../types';
import type { SessionTimer } from '../hooks/useSessionTimer';
import { formatTime } from '../utils';

interface RestPhaseProps {
  timer: SessionTimer;
  nextExercise: SessionExercise;
  isNewRound: boolean;
  restType?: RestType;
  currentSetNumber?: number;
  totalSets?: number;
  onSkip: () => void;
}

export function RestPhase({ timer, nextExercise, isNewRound, restType = 'exercise', currentSetNumber, totalSets, onSkip }: RestPhaseProps) {
  const isSetRest = restType === 'set';

  return (
    <div className="flex flex-col items-center gap-6 p-6">
      <h2 className="text-heading-lg text-gray-600 dark:text-textDark-secondary">
        {isSetRest ? 'Set Rest' : 'Rest'}
      </h2>

      {isNewRound && (
        <span
          className="rounded-full bg-brand-100 px-3 py-1 text-sm font-semibold text-brand-800 dark:bg-brand-900 dark:text-brand-200"
          data-testid="rest-new-round-badge"
        >
          New Round
        </span>
      )}

      {isSetRest && currentSetNumber != null && totalSets != null && (
        <span
          className="text-sm font-medium text-gray-500 dark:text-textDark-muted"
          data-testid="rest-set-counter"
        >
          Set {currentSetNumber} of {totalSets} complete
        </span>
      )}

      <span
        className="text-7xl font-mono font-bold tabular-nums text-brand-600 dark:text-brand-400"
        data-testid="rest-timer-countdown"
      >
        {formatTime(timer.timeRemaining)}
      </span>

      {/* Progress bar */}
      <div className="h-1.5 w-full max-w-lg overflow-hidden rounded-full bg-gray-200 dark:bg-dark-border">
        <div
          className="h-full rounded-full bg-brand-600 dark:bg-brand-400"
          style={{ width: `${(1 - timer.progress) * 100}%` }}
        />
      </div>

      {/* Next exercise card */}
      <div
        className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface"
        data-testid="rest-next-exercise"
      >
        <p className="text-meta text-gray-500 dark:text-textDark-muted">
          {isSetRest ? 'Next Set' : 'Up Next'}
        </p>
        <p className="text-body font-medium text-gray-900 dark:text-textDark-primary">
          {nextExercise.exerciseName}
        </p>
        <p className="text-meta text-gray-500 dark:text-textDark-muted">
          {nextExercise.exerciseMode === 'RepBased'
            ? `${nextExercise.targetReps} reps`
            : `${nextExercise.targetDurationSeconds}s`}
          {nextExercise.suggestedWeightKg != null && ` · ${nextExercise.suggestedWeightKg}kg`}
        </p>
      </div>

      <button
        onClick={onSkip}
        className="w-full max-w-lg rounded-md border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-dark-border dark:text-textDark-secondary dark:hover:bg-dark-elevated"
        data-testid="rest-timer-skip-button"
      >
        I'm Ready
      </button>
    </div>
  );
}
